import {zodResolver} from '@hookform/resolvers/zod';
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';

import {
	AlertInfo,
	CTAButtonFixed,
	MainFooter,
	MainHeader,
	MaskedInput,
	PageWrapperLayout,
	SendMessageModal,
} from 'presentation/components';
import {ModalError} from 'presentation/components/ModalError';
import {
	FeaturesEnum,
	HelpCenterEnum,
	HomeEnum,
	SendMessageAllowedHourEnum,
} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {translations} from 'presentation/translations-new';
import {masks} from 'presentation/utils';
import {HandleMessage} from 'presentation/components/HandleMessage';
import {SendMessagePageEnum} from '../../../../../tests/constants/HelpPageEnum';
import {CustomCTAButtonFixedAction} from '../authorizations/styles';
import {PendingDialog} from '../booking/components';
import {useMediaQueries} from '../hooks';
import {ModalErrorTimeout} from './components/ModalErrorTimeout';
import {ModalSuccess} from './components/ModalSuccess';
import {StyledCard} from './components/StyledCard';
import {SUBJECT_OPTIONS} from './constants/subjectOptions';
import {useSendMessage} from './hooks/useSendMessage';
import {IFormData, formSchema} from './schema';
import {isWithinAllowedHours} from 'presentation/utils/isWithinAllowedHours';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {
	title,
	form,
	modalError: {title: errorModalTitle, description: errorModalDescription, buttonResend},
} = translations['pt-br'].privateFlow.help.sendYourMessage;
const {notice, goToHome} = translations['pt-br'].commons;

const {reason, mail, phone, message, alertMessage, buttonSend, loadingMessage} = form;

export function SendYourMessage() {
	const navigate = useNavigate();
	const {isMobile} = useMediaQueries();
	const {colors} = useTheme();
	const [allowed, setAllowed] = useState(true);
	const {
		handleBack,
		handleSendMessage,
		resetMutation,
		isPending,
		openTimeoutErrorModal,
		openDefaultErrorModal,
		protocolValue,
		openSuccessModal,
	} = useSendMessage();

	const {currentEmail, currentPhone} = usePersonalData();
	const {isEnabled: isTalkToUsFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_TALK_TO_US_MESSAGE,
	);

	const methods = useForm<IFormData>({
		resolver: zodResolver(formSchema),
		mode: 'onSubmit',
		defaultValues: {
			phone: currentPhone ?? '',
			email: currentEmail ?? '',
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		watch,
		register,
		setValue,
		formState: {errors},
	} = methods;

	const messageField = watch('message');

	const handleBackInitial = (path: string) => {
		resetMutation();
		reset();
		navigate(path);
	};

	const updateAllowed = () => {
		const isAllowed = isWithinAllowedHours(
			SendMessageAllowedHourEnum.StartHour,
			SendMessageAllowedHourEnum.EndHour,
		);

		setAllowed(isAllowed);
	};

	useEffect(() => {
		let intervalId: NodeJS.Timeout;
		if (isTalkToUsFeatureFlagEnabled) {
			updateAllowed();
			intervalId = setInterval(updateAllowed, 60_000);
		}

		return () => clearInterval(intervalId);
	}, [isTalkToUsFeatureFlagEnabled]);

	return (
		<PageWrapperLayout title={title}>
			<MainHeader title={title} handleBack={handleBack} />
			<Stack
				p={isMobile ? 0 : 4}
				bgcolor={isMobile ? colors.brand.primary.white : 'transparent'}>
				<FormProvider {...methods}>
					<Stack
						component={'form'}
						onSubmit={handleSubmit(data => handleSendMessage(data))}>
						<StyledCard.Root>
							<StyledCard.Content>
								<Stack p={1}>
									<Typography
										fontSize={'1rem'}
										fontWeight={600}
										color={'primary'}>
										{reason.title}
									</Typography>
									<FormControl sx={{mt: 1}} fullWidth error={!!errors.subject}>
										<InputLabel id="reason-select-label">
											{reason.placeholder}
										</InputLabel>

										<Controller
											name="subject"
											control={control}
											render={({field}) => (
												<Select
													{...field}
													{...register('subject')}
													labelId="reason-select-label"
													id="reason-select"
													defaultValue={''}
													label={reason.placeholder}
													MenuProps={
														isMobile
															? {
																	PaperProps: {
																		sx: {
																			bgcolor:
																				colors.gray[
																					'54Percent'
																				],
																			display: 'flex',
																			justifyContent:
																				'center',
																			alignItems: 'center',
																			position: 'static',
																		},
																	},
																}
															: undefined
													}>
													{isMobile ? (
														<MenuItem disabled value="">
															{reason.placeholder}
														</MenuItem>
													) : null}
													{SUBJECT_OPTIONS.map(subject => (
														<MenuItem
															key={subject.value}
															value={subject.value}>
															{subject.label}
														</MenuItem>
													))}
												</Select>
											)}
										/>

										<Typography
											variant="caption"
											color="error"
											margin={'3px 14px 0px'}>
											{errors.subject?.message}
										</Typography>
									</FormControl>
								</Stack>
								<Stack p={1} mt={1}>
									<Typography
										fontSize={'1rem'}
										fontWeight={600}
										color={'primary'}>
										{mail.title}
									</Typography>

									<Controller
										name="email"
										control={control}
										render={({field}) => (
											<TextField
												{...field}
												{...register('email')}
												error={!!errors.email}
												helperText={errors.email?.message}
												label={mail.placeholder}
												sx={{mt: 1}}
											/>
										)}
									/>
								</Stack>
								<Stack p={1} mt={1}>
									<Typography
										fontSize={'1rem'}
										fontWeight={600}
										color={'primary'}>
										{phone.title}
									</Typography>
									<Controller
										name="phone"
										control={control}
										render={({field}) => (
											<MaskedInput
												{...field}
												{...register('phone')}
												$fieldName="phone"
												id="phone"
												inputMode="tel"
												maskOptions={masks.cellphone}
												autoComplete="current-phone"
												error={!!errors.phone}
												helperText={errors.phone?.message}
												label={phone.placeholder}
												handleClearQuery={() => setValue('phone', '')}
												sx={{mt: 1}}
											/>
										)}
									/>
								</Stack>
								<Stack p={1} mt={1}>
									<Stack
										direction={'row'}
										justifyContent={'space-between'}
										alignItems={'center'}>
										<Typography
											fontSize={'1rem'}
											fontWeight={600}
											color={'primary'}>
											{message.title}
										</Typography>
										<Typography
											fontSize={'0.75rem'}
											fontWeight={400}
											color={'text.secondary'}>
											{messageField?.length ?? 0}/4000
										</Typography>
									</Stack>

									<Controller
										name="message"
										control={control}
										render={({field}) => (
											<TextField
												{...field}
												{...register('message')}
												error={!!errors.message}
												helperText={message.info}
												label={message.placeholder}
												minRows={4}
												maxRows={4}
												sx={{mt: 1}}
												multiline
												slotProps={{htmlInput: {maxLength: 4000}}}
											/>
										)}
									/>
								</Stack>
								<Stack p={1} mt={1}>
									<AlertInfo.Root>
										<HandleMessage message={alertMessage} />
									</AlertInfo.Root>
								</Stack>
								<CTAButtonFixed.Root
									mt={3}
									direction={'row'}
									justifyContent={'end'}>
									<CustomCTAButtonFixedAction
										fullWidth={isMobile}
										type="submit"
										size="large"
										variant="contained"
										data-testid={SendMessagePageEnum.SEND_MESSAGE_BUTTON}>
										{buttonSend}
									</CustomCTAButtonFixedAction>
								</CTAButtonFixed.Root>
							</StyledCard.Content>
						</StyledCard.Root>
					</Stack>
				</FormProvider>
			</Stack>

			<PendingDialog open={isPending} text={loadingMessage} />

			<ModalError
				open={openDefaultErrorModal}
				onClose={resetMutation}
				headerTitle={notice}
				title={errorModalTitle}
				description={errorModalDescription}
				maxWidth={420}
				firstButton={{
					title: goToHome,
					handleClick: () => handleBackInitial(HomeEnum.HOME_PAGE),
				}}
				secondButton={{
					title: buttonResend,
					handleClick: () => {
						resetMutation();
						handleSubmit(data => handleSendMessage(data))();
					},
				}}
			/>

			<ModalErrorTimeout open={openTimeoutErrorModal} onBack={resetMutation} />

			<ModalSuccess
				open={openSuccessModal}
				onBack={() => handleBackInitial(HomeEnum.HOME_PAGE)}
				protocolValue={protocolValue}
			/>

			<SendMessageModal open={!allowed} onBack={() => navigate(HelpCenterEnum.HELP)} />

			<MainFooter customMarginBottom={isMobile ? '130px' : undefined} />
		</PageWrapperLayout>
	);
}
